/**
 * <ClaimScrubPanel/> — visualises the AI denial-prevention scrub for a single
 * claim. Used in the AdminClaimsPanel expanded row.
 *
 * Backend contract: POST /api/rcm/claims/{claim_id}/scrub returns
 *   { status, risk_score, risk_rationale, issues[], verified_clean[],
 *     recurrence_prevention, ai_unavailable, from_cache?, checked_at }
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  Loader2, Sparkles, ShieldCheck, AlertTriangle, OctagonAlert,
  CheckCircle2, Wrench, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { AISafetyBadge } from '../brand/AISafetyBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// status → visual mapping. Keep palette consistent with TrustBadge / StatusBadge
// usage across the admin so a reviewer sees the same "blocker = red" cue here.
const STATUS_META = {
  clean: {
    label: 'Clean',
    icon: ShieldCheck,
    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    barCls: 'bg-emerald-500',
    blurb: 'No blockers — safe to submit.',
  },
  hold: {
    label: 'Hold',
    icon: AlertTriangle,
    cls: 'bg-amber-100 text-amber-700 border-amber-200',
    barCls: 'bg-amber-500',
    blurb: 'Warnings only — review before submitting.',
  },
  blocked: {
    label: 'Blocked',
    icon: OctagonAlert,
    cls: 'bg-rose-100 text-rose-700 border-rose-200',
    barCls: 'bg-rose-500',
    blurb: 'At least one blocker — fix before submitting.',
  },
};

const SEVERITY_META = {
  blocker: { cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Blocker' },
  warning: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Warning' },
};

export const ClaimScrubPanel = ({ claimId, initialResult = null, getAuthHeader }) => {
  const [result, setResult] = useState(initialResult);
  const [running, setRunning] = useState(false);

  const runScrub = async () => {
    setRunning(true);
    try {
      const res = await axios.post(
        `${API}/rcm/claims/${claimId}/scrub`,
        {},
        getAuthHeader(),
      );
      setResult(res.data);
      if (res.data?.from_cache) {
        toast.info(`Showing cached scrub — next run available in ${res.data.next_scrub_in_sec || '30'}s.`);
      } else if (res.data?.ai_unavailable) {
        toast.warning('AI denial-prevention engine unavailable. Deterministic checks completed.');
      } else {
        toast.success('Scrub complete');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run scrub');
    } finally {
      setRunning(false);
    }
  };

  if (!result) {
    return (
      <div className="rounded-2xl border border-dashed border-cn-border bg-cn-bg/30 p-4" data-testid={`claim-scrub-empty-${claimId}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cn-primary" />
            <div>
              <p className="text-sm font-semibold text-cn-text dark:text-white">AI denial-prevention scrub</p>
              <p className="text-[11px] text-cn-text-muted">
                Eligibility · auth · ICD-10 specificity · NCCI/MUE · payer routing · timely-filing.
                Vertex AI BAA-chain; PHI redacted on the prompt.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={runScrub}
            disabled={running}
            className="cn-btn cn-btn-primary !py-2 text-xs disabled:opacity-50"
            data-testid={`claim-scrub-run-${claimId}`}
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Run AI scrub
          </button>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[result.status] || STATUS_META.hold;
  const Icon = meta.icon;
  const issuesByLevel = {
    blocker: (result.issues || []).filter((i) => i.severity === 'blocker'),
    warning: (result.issues || []).filter((i) => i.severity === 'warning'),
  };
  const orderedIssues = [...issuesByLevel.blocker, ...issuesByLevel.warning];

  return (
    <div className="space-y-3" data-testid={`claim-scrub-result-${claimId}`}>
      {/* AI provenance disclosure — silent absence is wrong here because the
          output IS AI-generated; clinicians actively look for this disclosure. */}
      <div className="flex justify-end">
        <AISafetyBadge surfaceId="rcm.claim_scrub" compact />
      </div>
      {/* Result header — status + score + re-run */}
      <div className={`rounded-2xl border p-4 ${meta.cls}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm" data-testid={`claim-scrub-status-${claimId}`}>{meta.label}</span>
                <span className="text-xs opacity-75">·</span>
                <span className="text-xs font-mono" data-testid={`claim-scrub-score-${claimId}`}>
                  Risk {result.risk_score}/100
                </span>
                {result.from_cache && (
                  <span className="text-[10px] uppercase tracking-wider font-bold opacity-60">cached</span>
                )}
                {result.ai_unavailable && (
                  <span className="text-[10px] uppercase tracking-wider font-bold bg-white/60 text-cn-text px-1.5 py-0.5 rounded">AI unavailable — deterministic only</span>
                )}
              </div>
              <p className="mt-1 text-xs opacity-90">{meta.blurb}</p>
              {result.risk_rationale && (
                <p className="mt-1 text-xs opacity-90 italic">{result.risk_rationale}</p>
              )}
              {result.checked_at && (
                <p className="mt-1 text-[10px] opacity-60">Checked {new Date(result.checked_at).toLocaleString()}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={runScrub}
            disabled={running}
            className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-xs bg-white/70 hover:bg-white disabled:opacity-50"
            data-testid={`claim-scrub-rerun-${claimId}`}
          >
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Re-run
          </button>
        </div>
        {/* Risk meter */}
        <div className="mt-3 h-1.5 rounded-full bg-white/40 overflow-hidden" aria-label="Denial risk meter">
          <div className={`h-full rounded-full ${meta.barCls}`} style={{ width: `${result.risk_score}%` }} />
        </div>
      </div>

      {/* Issues table */}
      {orderedIssues.length > 0 && (
        <div className="rounded-2xl border border-cn-border overflow-hidden" data-testid={`claim-scrub-issues-${claimId}`}>
          <div className="px-4 py-2 bg-cn-bg/60 border-b border-cn-border flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
              Issues found ({orderedIssues.length})
            </span>
            <span className="text-[10px] text-cn-text-muted">
              {issuesByLevel.blocker.length} blocker · {issuesByLevel.warning.length} warning
            </span>
          </div>
          <ul className="divide-y divide-cn-border">
            {orderedIssues.map((it, i) => {
              const sev = SEVERITY_META[it.severity] || SEVERITY_META.warning;
              return (
                <li key={i} className="p-3 text-sm" data-testid={`claim-scrub-issue-${claimId}-${i}`}>
                  <div className="flex flex-wrap items-start gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${sev.cls}`}>
                      {sev.label}
                    </span>
                    <span className="text-[11px] uppercase tracking-wider text-cn-text-muted">
                      {it.stage}
                    </span>
                  </div>
                  <p className="mt-1.5 font-medium text-cn-text dark:text-white">{it.issue}</p>
                  {it.rule && (
                    <p className="mt-0.5 text-[11px] text-cn-text-muted">
                      <span className="font-mono">{it.rule}</span>
                    </p>
                  )}
                  {it.fix && (
                    <p className="mt-1 flex items-start gap-1.5 text-xs text-cn-text">
                      <Wrench className="w-3 h-3 mt-0.5 text-cn-primary flex-shrink-0" />
                      <span><span className="font-semibold">Fix:</span> {it.fix}</span>
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Verified clean */}
      {(result.verified_clean || []).length > 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid={`claim-scrub-verified-${claimId}`}>
          <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">
            What was verified clean
          </p>
          <ul className="grid sm:grid-cols-2 gap-1 text-xs text-cn-text">
            {result.verified_clean.map((v, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 flex-shrink-0" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recurrence-prevention rule */}
      {result.recurrence_prevention && (
        <div className="rounded-2xl border border-cn-primary/20 bg-cn-primary/5 p-3" data-testid={`claim-scrub-prevention-${claimId}`}>
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-primary mb-1">
            Recurrence prevention
          </p>
          <p className="text-xs text-cn-text-muted">{result.recurrence_prevention}</p>
        </div>
      )}

      <p className="text-[10px] text-cn-text-muted">
        The scrub is advisory — never auto-submits. A credentialed coder reviews and approves the next step.
      </p>
    </div>
  );
};

export default ClaimScrubPanel;
